import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  Renderer2,
  Inject
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';

/**
 * Generic floating sidebar/panel component
 *
 * Reusable component that handles:
 * - Open/close state
 * - Backdrop overlay
 * - Slide-in animation
 * - ESC key to close
 * - Click outside to close
 *
 * Content is provided via ng-content projection
 *
 * Usage:
 * <app-floating-sidebar
 *   [isOpen]="isSidebarOpen"
 *   [title]="'Mi Panel'"
 *   (close)="closeSidebar()"
 * >
 *   <!-- Custom content here -->
 *   <app-filters-form></app-filters-form>
 * </app-floating-sidebar>
 */
@Component({
  selector: 'app-floating-sidebar',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './floating-sidebar.component.html',
  styleUrls: ['./floating-sidebar.component.scss']
})
export class FloatingSidebarComponent implements OnChanges {
  /**
   * Control sidebar visibility
   */
  @Input() isOpen = false;

  /**
   * Sidebar title
   */
  @Input() title = '';

  /**
   * Custom width (default: 21.5625rem)
   */
  @Input() width = '21.5625rem';

  /**
   * Event emitted when sidebar should be closed
   */
  @Output() closePanel = new EventEmitter<void>();

  /**
   * Animation state for slide in/out
   */
  isAnimating = false;
  showContent = false;

  constructor(
    private readonly renderer: Renderer2,
    @Inject(DOCUMENT) private readonly document: Document
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']) {
      if (this.isOpen) {
        this.showContent = true;
        this.isAnimating = false;
        this.renderer.setStyle(this.document.body, 'overflow', 'hidden');
      } else {
        this.isAnimating = true;
        setTimeout(() => {
          this.showContent = false;
          this.isAnimating = false;
        }, 400); // Match animation duration
        this.renderer.removeStyle(this.document.body, 'overflow');
      }
    }
  }

  /**
   * Close the sidebar
   */
  onClose(): void {
    this.closePanel.emit();
  }

  /**
   * Handle backdrop click
   */
  onBackdropClick(): void {
    this.onClose();
  }

  /**
   * Prevent sidebar click from closing
   */
  onSidebarClick(event: Event): void {
    event.stopPropagation();
  }
}
